import { RetellWebClient } from 'retell-client-js-sdk';

const BACKEND_API = '/api';

let retellWebClient = null;
let audioStream = null;

export const retellService = {
  // Fetch list of agents via backend proxy
  async fetchAgents() {
    try {
      const response = await fetch(`${BACKEND_API}/list-agents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return [];
      }
      
      const data = await response.json();
      const agentsList = data.agents || [];
      
      return agentsList.map(agent => {
        const fullName = agent.agent_name || 'AI Agent';
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0];
        const initials = nameParts.length > 1 
          ? nameParts[0][0] + nameParts[nameParts.length - 1][0]
          : fullName.slice(0, 2);
        // v2 list-agents returns channel; voice_id may be absent on some payloads
        const isVoice = agent.channel === 'voice' || Boolean(agent.voice_id);

        return {
          id: agent.agent_id,
          name: fullName,
          firstName: firstName,
          initials: initials.toUpperCase(),
          role: isVoice ? 'Voice Agent' : 'AI Assistant',
          voiceId: agent.voice_id,
          type: isVoice ? 'Voice Agent' : 'AI Assistant',
        };
      });
      
    } catch (error) {
      return [];
    }
  },

  // Create a web call via backend proxy
  async createWebCall(agentId) {
    try {
      const response = await fetch(`${BACKEND_API}/create-web-call`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: agentId,
          metadata: { source: 'website_demo' }
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create web call: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      throw error;
    }
  },

  // Start the web call using Retell SDK
  async startCall(callData, deviceId = null) {
    try {
      // Get microphone access
      const constraints = deviceId 
        ? { 
            audio: {
              deviceId: { exact: deviceId },
              echoCancellation: true,
              noiseSuppression: true,
              sampleRate: 24000,
            } 
          }
        : {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              sampleRate: 24000,
            }
          };
      
      audioStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Initialize Retell client
      if (!retellWebClient) {
        retellWebClient = new RetellWebClient();
      }

      // Extract access token
      let accessToken;
      if (typeof callData === 'string') {
        accessToken = callData;
      } else {
        accessToken = callData.access_token || callData.accessToken || callData.token;
      }
      
      if (!accessToken) {
        throw new Error('No access token received from backend');
      }

      // Start the call
      await retellWebClient.startCall({
        accessToken: accessToken,
        sampleRate: 24000,
        captureDeviceId: audioStream.getAudioTracks()[0].getSettings().deviceId || 'default',
      });
      
      return retellWebClient;
    } catch (error) {
      // Clean up audio stream if it was created
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
        audioStream = null;
      }
      
      throw error;
    }
  },

  // Stop the web call
  stopCall() {
    if (retellWebClient) {
      try {
        retellWebClient.stopCall();
      } catch (error) {
        // Silently handle error
      }
    }
    
    // Stop audio stream
    if (audioStream) {
      audioStream.getTracks().forEach(track => track.stop());
      audioStream = null;
    }
  },

  // Event listeners
  on(event, callback) {
    if (retellWebClient) {
      retellWebClient.on(event, callback);
    }
  },

  off(event, callback) {
    if (retellWebClient) {
      retellWebClient.off(event, callback);
    }
  },

  // Check if client exists
  isClientActive() {
    return retellWebClient !== null;
  }
};
